/**
 * Lenavio Talent Map — top-level admin page.
 *
 * Three tabs:
 *
 *   1. **Skills library** — searchable list, create/edit, soft-delete.
 *   2. **Heatmap** — department × skill grid with proficiency
 *      colour intensity. Pure CSS, no chart library.
 *   3. **Gaps** — role-level red flags + department coverage.
 *
 * The employee skill profile lives at /talent/employees/{id}
 * (separate page) — too dense to live inside a tab.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Edit3,
  GraduationCap,
  Plus,
  Search,
  Trash2,
} from "lucide-react";

import {
  createSkill,
  deleteSkill,
  getGapReport,
  getHeatmap,
  listSkills,
  updateSkill,
  type GapReport,
  type HeatmapResponse,
  type Skill,
  type SkillCategory,
} from "@/api/talent";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Tabs } from "@/components/ui/Tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

const CATEGORY_OPTIONS: { value: SkillCategory; label: string }[] = [
  { value: "technical", label: "Technical" },
  { value: "soft", label: "Soft" },
  { value: "leadership", label: "Leadership" },
  { value: "domain", label: "Domain" },
  { value: "certification", label: "Certification" },
];

export function TalentMapPage() {
  const [tab, setTab] = useState<"library" | "heatmap" | "gaps">("library");

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Talent Map"
        subtitle="Skills library, heatmap, and gap analysis."
      />

      <Tabs
        tabs={[
          { id: "library", label: "Skills library" },
          { id: "heatmap", label: "Heatmap" },
          { id: "gaps", label: "Gap analysis" },
        ]}
        activeTab={tab}
        onChange={(v) => setTab(v as "library" | "heatmap" | "gaps")}
        className="mb-6"
      />

      {tab === "library" && <SkillsLibrary />}
      {tab === "heatmap" && <Heatmap />}
      {tab === "gaps" && <Gaps />}
    </motion.div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Library
// ════════════════════════════════════════════════════════════════════


function SkillsLibrary() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<Skill[] | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Skill | "new" | null>(null);

  const refresh = async () => {
    try {
      setRows(await listSkills());
    } catch {
      addToast("error", "Could not load skills.");
    }
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const handleDelete = async (s: Skill) => {
    if (!confirm(`Archive "${s.name}"? Existing employee + role rows stay.`)) {
      return;
    }
    try {
      await deleteSkill(s.id);
      addToast("success", "Archived.");
      void refresh();
    } catch {
      addToast("error", "Could not archive skill.");
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
        <Button onClick={() => setEditing("new")}>
          <Plus className="h-4 w-4" />
          New skill
        </Button>
      </div>

      {visible === null ? (
        <div className="grid grid-cols-1 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 rounded bg-border/30 animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card className="text-center py-10">
          <GraduationCap className="h-8 w-8 text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-muted">No skills match your search.</p>
        </Card>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
          {visible.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {s.name}
                  </p>
                  <Badge variant="info" className="capitalize">
                    {s.category}
                  </Badge>
                  {s.is_default_template && (
                    <Badge variant="default" className="uppercase text-[10px] tracking-wide">
                      default
                    </Badge>
                  )}
                  {!s.is_active && <Badge variant="warning">Archived</Badge>}
                </div>
                {s.description && (
                  <p className="text-xs text-text-muted line-clamp-1">
                    {s.description}
                  </p>
                )}
                <p className="text-[11px] text-text-muted mt-0.5">
                  {s.employee_count} employee
                  {s.employee_count === 1 ? "" : "s"}
                  {s.avg_proficiency !== null
                    ? ` · avg ${s.avg_proficiency.toFixed(1)}/4`
                    : ""}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(s)}
                  aria-label="Edit"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(s)}
                  aria-label="Archive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <SkillFormModal
          skill={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}


function SkillFormModal({
  skill,
  onClose,
  onSaved,
}: {
  skill: Skill | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = skill !== null;
  const { addToast } = useToast();
  const [name, setName] = useState(skill?.name || "");
  const [category, setCategory] = useState<SkillCategory>(
    (skill?.category as SkillCategory) || "technical",
  );
  const [description, setDescription] = useState(skill?.description || "");
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      addToast("error", "Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && skill) {
        await updateSkill(skill.id, {
          name: name.trim(),
          category,
          description: description.trim() || undefined,
        });
      } else {
        await createSkill({
          name: name.trim(),
          category,
          description: description.trim() || undefined,
        });
      }
      addToast("success", "Saved.");
      onSaved();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not save skill.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={isEdit ? "Edit skill" : "New skill"}>
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value as SkillCategory)}
          options={CATEGORY_OPTIONS}
        />
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={save} isLoading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════════════
// Heatmap
// ════════════════════════════════════════════════════════════════════


function Heatmap() {
  const { addToast } = useToast();
  const [data, setData] = useState<HeatmapResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    getHeatmap()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => addToast("error", "Could not load heatmap."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === null) {
    return <div className="h-64 rounded bg-border/30 animate-pulse" />;
  }
  if (data.cells.length === 0) {
    return (
      <Card className="text-center py-10 text-sm text-text-muted">
        No skills logged yet. Have employees fill out their skill profile,
        or run AI Assess from a profile to seed.
      </Card>
    );
  }

  // Build a (dept, skill) → cell map for O(1) lookup during render.
  const cellMap = new Map<string, (typeof data.cells)[number]>();
  for (const c of data.cells) {
    cellMap.set(`${c.department_name}|${c.skill_name}`, c);
  }

  const colourFor = (avg: number): string => {
    // 0..4 mapped to green-shades.
    const ratio = Math.max(0, Math.min(avg / 4, 1));
    const alpha = 0.15 + ratio * 0.65;
    return `rgba(16, 185, 129, ${alpha.toFixed(2)})`;
  };

  return (
    <Card padding={false} className="overflow-x-auto">
      <table className="text-xs w-full">
        <thead className="bg-background border-b border-border">
          <tr>
            <th className="sticky left-0 bg-background border-r border-border px-3 py-2 text-left font-medium text-text-muted">
              Department \ Skill
            </th>
            {data.skills.map((s) => (
              <th
                key={s}
                className="px-2 py-2 text-left font-medium text-text-muted whitespace-nowrap"
              >
                {s}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.departments.map((d) => (
            <tr key={d} className="border-b border-border/60">
              <td className="sticky left-0 bg-surface border-r border-border px-3 py-2 font-medium text-text-primary whitespace-nowrap">
                {d}
              </td>
              {data.skills.map((s) => {
                const cell = cellMap.get(`${d}|${s}`);
                if (!cell) {
                  return (
                    <td
                      key={s}
                      className="px-2 py-2 text-text-muted text-center"
                      title={`${d} · ${s}: no holders`}
                    >
                      —
                    </td>
                  );
                }
                return (
                  <td
                    key={s}
                    className="px-2 py-2 text-center"
                    style={{ background: colourFor(cell.avg_proficiency) }}
                    title={`${d} · ${s}: avg ${cell.avg_proficiency.toFixed(1)}, ${cell.holder_count} holder(s)`}
                  >
                    <span className="font-medium text-text-primary">
                      {cell.avg_proficiency.toFixed(1)}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}


// ════════════════════════════════════════════════════════════════════
// Gap analysis
// ════════════════════════════════════════════════════════════════════


function Gaps() {
  const { addToast } = useToast();
  const [data, setData] = useState<GapReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    getGapReport()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => addToast("error", "Could not load gap report."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === null) {
    return (
      <div className="grid grid-cols-1 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded bg-border/30 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
          Roles
        </h3>
        {data.roles.length === 0 ? (
          <Card className="text-center py-6 text-sm text-text-muted">
            No role requirements set yet. Define them per role for the
            gap report.
          </Card>
        ) : (
          <ul className="space-y-2">
            {data.roles.map((r) => {
              const tone =
                r.critical_gaps > 0
                  ? "border-danger/40 bg-danger/5"
                  : r.total_gaps > 0
                    ? "border-amber-500/40 bg-amber-500/5"
                    : "border-success/40 bg-success/5";
              return (
                <Card key={r.role_title} className={tone}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-text-primary">
                        {r.role_title}
                      </p>
                      <p className="text-xs text-text-muted">
                        {r.employee_count} holder
                        {r.employee_count === 1 ? "" : "s"} · {r.total_gaps}{" "}
                        gap{r.total_gaps === 1 ? "" : "s"} ·{" "}
                        <span className="text-danger font-medium">
                          {r.critical_gaps} critical
                        </span>
                      </p>
                      {r.missing_critical_skills.length > 0 && (
                        <p className="text-xs text-danger mt-1.5">
                          <AlertTriangle className="inline h-3 w-3 mr-1" />
                          Nobody covers:{" "}
                          {r.missing_critical_skills.join(", ")}
                        </p>
                      )}
                    </div>
                    {r.critical_gaps > 0 && (
                      <Badge variant="danger">Critical gap</Badge>
                    )}
                  </div>
                </Card>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
          Departments
        </h3>
        {data.departments.length === 0 ? (
          <Card className="text-center py-6 text-sm text-text-muted">
            No department-level data yet.
          </Card>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
            {data.departments.map((d) => (
              <li
                key={d.department_name + (d.department_id ?? "none")}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {d.department_name}
                  </p>
                  <p className="text-xs text-text-muted">
                    {d.employees} employee{d.employees === 1 ? "" : "s"} ·{" "}
                    {d.avg_proficiency !== null
                      ? `avg ${d.avg_proficiency.toFixed(1)}/4`
                      : "no skills logged"}
                    {d.weakest_skills.length > 0
                      ? ` · weak: ${d.weakest_skills.join(", ")}`
                      : ""}
                  </p>
                </div>
                <Badge
                  variant={
                    d.coverage >= 0.8
                      ? "success"
                      : d.coverage >= 0.5
                        ? "warning"
                        : "danger"
                  }
                >
                  {(d.coverage * 100).toFixed(0)}% covered
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
